import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Form, Table, Button, Alert } from 'react-bootstrap';

function FleetWizard() {
  const [blueprintUsers, setBlueprintUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [hireCategoryGroups, setHireCategoryGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [hireCategories, setHireCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [hireStockItems, setHireStockItems] = useState([]);
  const [selectedStockItem, setSelectedStockItem] = useState('');
  const [gridItems, setGridItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingStockItems, setLoadingStockItems] = useState(false);
  const [error, setError] = useState('');

  const fetchBlueprintUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users/blueprint');
      setBlueprintUsers(response.data);
    } catch (error) {
      setError('Failed to fetch blueprint users');
      console.error('Error fetching blueprint users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHireCategoryGroups = async (userId) => {
    if (!userId) {
      setHireCategoryGroups([]);
      setSelectedGroup('');
      setHireCategories([]);
      setSelectedCategory('');
      setHireStockItems([]);
      setSelectedStockItem('');
      return;
    }

    try {
      setLoadingGroups(true);
      const response = await axios.get('/api/hire-category-groups/blueprint', {
        headers: userId ? { 'X-Blueprint-User-Id': String(userId) } : {},
      });
      setHireCategoryGroups(response.data);
      setSelectedGroup(''); // Reset selection when groups change
      setHireCategories([]);
      setSelectedCategory('');
      setHireStockItems([]);
      setSelectedStockItem('');
    } catch (error) {
      console.error('Error fetching hire category groups:', error);
      setHireCategoryGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchHireCategories = async (userId, groupName) => {
    if (!userId || !groupName) {
      setHireCategories([]);
      setSelectedCategory('');
      setHireStockItems([]);
      setSelectedStockItem('');
      return;
    }

    try {
      setLoadingCategories(true);
      console.log('Fetching hire categories for userId:', userId, 'groupName:', groupName);
      const response = await axios.get(
        `/api/hire-categories/group/${encodeURIComponent(groupName)}`,
        {
          headers: userId ? { 'X-Blueprint-User-Id': String(userId) } : {},
        }
      );
      console.log('Hire categories response:', response.data);
      setHireCategories(response.data);
      setSelectedCategory(''); // Reset selection when categories change
      setHireStockItems([]);
      setSelectedStockItem('');
    } catch (error) {
      console.error('Error fetching hire categories:', error);
      setHireCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchHireStockItems = async (userId, categoryCode) => {
    if (!userId || !categoryCode) {
      setHireStockItems([]);
      setSelectedStockItem('');
      return;
    }

    try {
      setLoadingStockItems(true);
      console.log('Fetching hire stock items for userId:', userId, 'categoryCode:', categoryCode);
      const response = await axios.get(
        `/api/hire-stock/category/${encodeURIComponent(categoryCode)}`,
        {
          headers: userId ? { 'X-Blueprint-User-Id': String(userId) } : {},
        }
      );
      console.log('Hire stock items response:', response.data);
      setHireStockItems(response.data);
      setSelectedStockItem(''); // Reset selection when stock items change
    } catch (error) {
      console.error('Error fetching hire stock items:', error);
      setHireStockItems([]);
    } finally {
      setLoadingStockItems(false);
    }
  };

  useEffect(() => {
    fetchBlueprintUsers();
  }, []);

  const handleUserChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedUser(selectedValue);

    if (selectedValue) {
      // Find the selected user to get their ID
      const user = blueprintUsers.find((u) => u.username === selectedValue);
      if (user) {
        setSelectedUserId(user.id);
        fetchHireCategoryGroups(user.id);
      }
    } else {
      setSelectedUserId('');
      fetchHireCategoryGroups('');
    }
  };

  const handleGroupChange = (e) => {
    const selectedGroupValue = e.target.value;
    setSelectedGroup(selectedGroupValue);

    if (selectedGroupValue && selectedUserId) {
      fetchHireCategories(selectedUserId, selectedGroupValue);
    } else {
      setHireCategories([]);
      setSelectedCategory('');
    }
  };

  const handleCategoryChange = (e) => {
    const selectedCategoryValue = e.target.value;
    setSelectedCategory(selectedCategoryValue);

    if (selectedCategoryValue && selectedUserId) {
      fetchHireStockItems(selectedUserId, selectedCategoryValue);
    } else {
      setHireStockItems([]);
      setSelectedStockItem('');
    }
  };

  const handleStockItemChange = (e) => {
    const selectedStockItemValue = e.target.value;
    setSelectedStockItem(selectedStockItemValue);

    if (selectedStockItemValue) {
      // Find the selected stock item details
      const selectedItem = hireStockItems.find((item) => item.stock_no === selectedStockItemValue);
      if (selectedItem) {
        // Get the source stock number and analyze its structure
        const sourceStockNo = selectedItem.stock_no;

        // Parse the source stock number to find prefix and numeric part
        // Match: everything up to and including trailing digits
        const match = sourceStockNo.match(/^(.*?)(\d+)$/);

        let stockNumber = sourceStockNo;

        if (match) {
          const prefix = match[1]; // e.g., "S" from "S003"
          const numericStr = match[2]; // e.g., "003" from "S003"
          const padLength = numericStr.length; // e.g., 3
          const baseNumber = parseInt(numericStr, 10); // e.g., 3

          // Find all grid items that match this prefix pattern
          const matchingItems = gridItems.filter((item) => {
            const itemMatch = item.stock_no.match(/^(.*?)(\d+)$/);
            return itemMatch && itemMatch[1] === prefix;
          });

          // Also include the source item's number in our search
          let highestNumber = baseNumber;

          // Find the highest number among matching items
          for (const item of matchingItems) {
            const itemMatch = item.stock_no.match(/^(.*?)(\d+)$/);
            if (itemMatch) {
              const itemNum = parseInt(itemMatch[2], 10);
              if (itemNum >= highestNumber) {
                highestNumber = itemNum;
              }
            }
          }

          // If there are matching items, increment from the highest
          if (matchingItems.length > 0) {
            const nextNumber = highestNumber + 1;
            stockNumber = prefix + String(nextNumber).padStart(padLength, '0');
          }
          // Otherwise, use the source stock number as-is (first copy)
        }
        // If no numeric part found, use stock number as-is

        const newGridItem = {
          id: Date.now(), // Simple ID for React key
          category: selectedCategory,
          stock_no: stockNumber,
          base_stock_no: selectedItem.stock_no, // Keep original for tracking duplicates
          name: selectedItem.name,
          serial_number: '', // Empty by default, user can edit
          purchase_date: '', // Empty by default
          purchase_price: undefined, // Empty by default
        };
        setGridItems((prev) => [...prev, newGridItem]);
      }
    }
  };

  const handleGridCellChange = (itemId, field, value) => {
    setGridItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveGridItem = (itemId) => {
    setGridItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const copyFleetData = async () => {
    if (!selectedUserId || !gridItems || gridItems.length === 0) {
      setError('Please select a user and add items to the grid before copying fleet data.');
      return;
    }

    try {
      console.log('Copying fleet data for items:', gridItems);
      const response = await axios.post('/api/fleet-wizard/copy-fleet-data', {
        sourceUserId: selectedUserId,
        fleetItems: gridItems,
      });

      console.log('Fleet data copy results:', response.data);

      // Show success message
      const results = response.data;
      let successMessage = `Fleet data copied successfully!\n`;
      successMessage += `• Hire Categories: ${results.hireCategoriesCopied}\n`;
      successMessage += `• Hire Category Groups: ${results.hireCategoryGroupsCopied}\n`;
      successMessage += `• Hire Stock Items: ${results.hireStockCopied}\n`;
      successMessage += `• Inclusions: ${results.inclusionsCopied}\n`;
      successMessage += `• Fleet Reminders: ${results.fleetRemindersCopied}\n`;
      successMessage += `• Inspection Tests: ${results.inspectionTestsCopied}\n`;
      if (results.salesItemsCopied > 0) {
        successMessage += `• Sales Items: ${results.salesItemsCopied}\n`;
      }

      alert(successMessage);

      // Show warnings if there were errors
      if (results.errors && results.errors.length > 0) {
        console.warn('Some errors occurred during copying:', results.errors);
        setError('Some items had errors: ' + results.errors.join(', '));
      }
    } catch (error) {
      console.error('Error copying fleet data:', error);
      setError('Failed to copy fleet data: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <i className="fas fa-magic me-3 text-primary" style={{ fontSize: '2.5rem' }}></i>
              <div>
                <h1 className="mb-0">Fleet Wizard</h1>
                <p className="text-muted mb-0">Copy fleet items from blueprint configurations</p>
              </div>
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <div>Loading blueprint users...</div>
                    ) : (
                      <Form.Select value={selectedUser} onChange={handleUserChange}>
                        <option value="">Select a blueprint...</option>
                        {blueprintUsers.map((user) => (
                          <option key={user.id} value={user.username}>
                            {user.username}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    {!selectedUser ? (
                      <div className="text-muted">Please select a blueprint first</div>
                    ) : loadingGroups ? (
                      <div>Loading groups...</div>
                    ) : (
                      <Form.Select
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        disabled={!selectedUser}
                      >
                        <option value="">Select a Group...</option>
                        {hireCategoryGroups.map((group) => (
                          <option key={group.id} value={group.group_name}>
                            {group.group_code} {group.group_name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    {!selectedGroup ? (
                      <div className="text-muted">Please select a group first</div>
                    ) : loadingCategories ? (
                      <div>Loading categories...</div>
                    ) : (
                      <Form.Select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        disabled={!selectedGroup}
                      >
                        <option value="">Select a Category...</option>
                        {hireCategories.map((category) => (
                          <option key={category.id} value={category.code}>
                            {category.code} - {category.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    {!selectedCategory ? (
                      <div className="text-muted">Please select a category first</div>
                    ) : loadingStockItems ? (
                      <div>Loading assets...</div>
                    ) : (
                      <Form.Select
                        value={selectedStockItem}
                        onChange={handleStockItemChange}
                        disabled={!selectedCategory}
                      >
                        <option value="">Select an Asset...</option>
                        {/* Get unique names and map them to options */}
                        {Array.from(new Set(hireStockItems.map((item) => item.name)))
                          .sort()
                          .map((uniqueName) => {
                            // Find the first item with this name to get its stock_no for the value
                            const firstItemWithName = hireStockItems.find(
                              (item) => item.name === uniqueName
                            );
                            return (
                              <option key={uniqueName} value={firstItemWithName.stock_no}>
                                {uniqueName}
                              </option>
                            );
                          })}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Grid for selected items */}
          {gridItems.length > 0 && (
            <Row className="mt-4">
              <Col>
                <Card>
                  <Card.Header>
                    <h5>Selected Fleet Items</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Stock Number</th>
                          <th>Name</th>
                          <th>Serial Number</th>
                          <th>Purchase Date</th>
                          <th>Purchase Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gridItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div
                                className="p-2 bg-light text-muted"
                                style={{ fontSize: '0.875rem' }}
                              >
                                {item.category}
                              </div>
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.stock_no}
                                onChange={(e) =>
                                  handleGridCellChange(
                                    item.id,
                                    'stock_no',
                                    e.target.value.toUpperCase()
                                  )
                                }
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.name}
                                onChange={(e) =>
                                  handleGridCellChange(item.id, 'name', e.target.value)
                                }
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.serial_number}
                                onChange={(e) =>
                                  handleGridCellChange(item.id, 'serial_number', e.target.value)
                                }
                                placeholder="Enter serial number"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="date"
                                value={item.purchase_date}
                                onChange={(e) =>
                                  handleGridCellChange(item.id, 'purchase_date', e.target.value)
                                }
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.purchase_price || ''}
                                onChange={(e) =>
                                  handleGridCellChange(item.id, 'purchase_price', e.target.value)
                                }
                                placeholder="0.00"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleRemoveGridItem(item.id)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <div className="d-flex justify-content-center mt-3">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={copyFleetData}
                        disabled={gridItems.length === 0 || !selectedUserId}
                      >
                        Add to Fleet ({gridItems.length} items)
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default FleetWizard;
